import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';

// Raw organization data from API
interface OrganizationData {
  id: string;
  name: string;
  subscriptionTier: string;
  maxProducts: number;
  currentProducts: number;
  maxClients: number;
  currentClients: number;
  salesRateLimit: number;
  offlineTransactionLimit: number;
  maxLocations: number;
  currentLocations: number;
  includedStores: number;
  currentStores: number;
  includedWarehouses: number;
  currentWarehouses: number;
  includedStaff: number;
  currentStaff: number;
  isVirtualOnly: boolean;
  hasStorefrontAccess: boolean;
  hasAnalyticsAccess: boolean;
}

// Transformed data for the component
export interface OrganizationLimits {
  tier: string;
  limits: {
    maxProducts: number;
    currentProducts: number;
    maxClients: number;
    currentClients: number;
    salesRateLimit: number;
    offlineTransactionLimit: number;
    maxLocations: number;
    currentLocations: number;
    includedStores: number;
    currentStores: number;
    includedWarehouses: number;
    currentWarehouses: number;
    includedStaff: number;
    currentStaff: number;
  };
  isVirtualOnly: boolean;
  hasStorefrontAccess: boolean;
  hasAnalyticsAccess: boolean;
}

export interface UpdateLimitsDto {
  includedStores?: number;
  includedWarehouses?: number;
  includedStaff?: number;
  maxProducts?: number;
  maxClients?: number;
  maxLocations?: number;
  salesRateLimit?: number;
  offlineTransactionLimit?: number;
  isVirtualOnly?: boolean;
  hasStorefrontAccess?: boolean;
  hasAnalyticsAccess?: boolean;
  pricePerExtraStore?: number;
  pricePerExtraWarehouse?: number;
  pricePerExtraStaff?: number;
}

const QUERY_KEYS = {
  orgLimits: (orgId: string) => ['organizations', orgId, 'limits'] as const,
};

// Transform organization data to the expected format
function transformOrgToLimits(org: OrganizationData): OrganizationLimits {
  return {
    tier: org.subscriptionTier || 'free',
    limits: {
      maxProducts: org.maxProducts ?? 50,
      currentProducts: org.currentProducts ?? 0,
      maxClients: org.maxClients ?? 100,
      currentClients: org.currentClients ?? 0,
      salesRateLimit: org.salesRateLimit ?? 10,
      offlineTransactionLimit: org.offlineTransactionLimit ?? 100,
      maxLocations: org.maxLocations ?? 1,
      currentLocations: org.currentLocations ?? 0,
      includedStores: org.includedStores ?? 1,
      currentStores: org.currentStores ?? 0,
      includedWarehouses: org.includedWarehouses ?? 0,
      currentWarehouses: org.currentWarehouses ?? 0,
      includedStaff: org.includedStaff ?? 2,
      currentStaff: org.currentStaff ?? 0,
    },
    isVirtualOnly: org.isVirtualOnly ?? true,
    hasStorefrontAccess: org.hasStorefrontAccess ?? false,
    hasAnalyticsAccess: org.hasAnalyticsAccess ?? false,
  };
}

export const useOrganizationLimits = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.orgLimits(orgId),
    queryFn: async (): Promise<OrganizationLimits> => {
      // Fetch the full organization data which includes all limit fields
      const { data } = await axiosInstance.get<OrganizationData>(
        `/organizations/${orgId}`
      );
      return transformOrgToLimits(data);
    },
    enabled: !!orgId,
  });
};

export const useUpdateOrganizationLimits = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateLimitsDto) => {
      const { data } = await axiosInstance.patch(
        `/organizations/${orgId}/limits`,
        dto
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgLimits(orgId) });
      // Also invalidate the organization query in case it's being used elsewhere
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
    },
  });
};
